/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { authSwitchScopes } from "../funcs/authSwitchScopes.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SwitchAuthScopesRequest,
  SwitchAuthScopesResponse,
  SwitchAuthScopesSecurity,
} from "../models/operations/switchauthscopes.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SwitchScopesMutationVariables = {
  request?: SwitchAuthScopesRequest | undefined;
  security?: SwitchAuthScopesSecurity | undefined;
  options?: RequestOptions;
};

export type SwitchScopesMutationData = SwitchAuthScopesResponse | undefined;

export type SwitchScopesMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * switchScopes auth
 *
 * @remarks
 * Switches the authentication scope to a different organization.
 */
export function useSwitchScopesMutation(
  options?: MutationHookOptions<
    SwitchScopesMutationData,
    SwitchScopesMutationError,
    SwitchScopesMutationVariables
  >,
): UseMutationResult<
  SwitchScopesMutationData,
  SwitchScopesMutationError,
  SwitchScopesMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSwitchScopesMutation(client, options),
    ...options,
  });
}

export function mutationKeySwitchScopes(): MutationKey {
  return ["@gram/client", "auth", "switchScopes"];
}

export function buildSwitchScopesMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SwitchScopesMutationVariables,
  ) => Promise<SwitchScopesMutationData>;
} {
  return {
    mutationKey: mutationKeySwitchScopes(),
    mutationFn: function switchScopesMutationFn({
      request,
      security,
      options,
    }): Promise<SwitchScopesMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(authSwitchScopes(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
